import React, { useCallback, useEffect, useState } from 'react';
import './SearchStations.scss';
import { useNavigate } from 'react-router-dom';
import Input from '../../../../Components/Common/Input/Input';
import FilterBtn from '../../../../Components/Common/FilterBtn/FilterBtn';
import MiniStationCard from '../../../../Components/MiniStationCard/MiniStationCard';
import { Pathname } from '../../../../Router/Pathname';
import { useDispatch, useSelector } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroller';
import { filterStations, fetchConnectorStandards, getallStations } from '../redux/action';
import {
    resolveIsFavourite,
    setFavouriteOverride,
} from '../../FavouriteStationsView/redux/favouriteStationSlice';
import PreLoader from '../../../../Assets/icons/PreLoader/PreLoader';
import StationHeader from './StationHeader';
import {
    useFavouriteToggle,
    usePullToRefresh,
    useRefreshOnFocus,
} from '../../../../Hooks';
import {
    getStationStatus,
    prettyConnectorStandard,
    STATION_STATUS,
} from '../../../../Utils';

const ALL_CHIP = 'all';
const FAVOURITES_CHIP = 'favourites';

const SearchStations = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Destructuring props and state
    const { stations } = useSelector(({ stations }) => ({ stations }));
    const { allStations, loading, connectorStandards, hasMore, page } = stations;
    const { favouriteOverrides } = useSelector((store) => store.favouriteStations);

    // State — one chip at a time: 'all', 'favourites', or a connector standard.
    const [selectedChip, setSelectedChip] = useState(ALL_CHIP);
    const [stationName, setStationName] = useState(null);

    const favouritesOnly = selectedChip === FAVOURITES_CHIP;
    const activeFilter =
        selectedChip === ALL_CHIP || favouritesOnly ? null : selectedChip;

    // Event handlers
    const handleStationName = (val) => setStationName(val);
    const handleCardNavigation = (chargingStation) => {
        if (getStationStatus(chargingStation) === STATION_STATUS.UNAVAILABLE) return;

        navigate(Pathname.SEARCH_CONNECTOR, {
            state: {
                id: chargingStation.id,
                name: chargingStation.station_name,
                address: chargingStation.address,
                is_favourite: resolveIsFavourite(favouriteOverrides, chargingStation),
            },
        })
    }

    const applyLocalFavourite = useCallback(
        (stationId, isFavourite) => {
            dispatch(setFavouriteOverride({ id: stationId, is_favourite: isFavourite }));
        },
        [dispatch]
    );

    const { toggleFavourite } = useFavouriteToggle({ applyLocal: applyLocalFavourite });

    // Components
    const Preloader = () => (
        <div className="SearchStations__Loader" key={0}>
            <PreLoader />
        </div>
    );

    // Rendering station cards
    const renderStationCards = () =>
        allStations?.map((chargingStation) => {
            const isFavourite = resolveIsFavourite(favouriteOverrides, chargingStation);
            return (
                <MiniStationCard
                    key={chargingStation?.id}
                    name={chargingStation?.station_name}
                    address={`${chargingStation?.address}`}
                    status={getStationStatus(chargingStation)}
                    isFavourite={isFavourite}
                    onToggleFavourite={() =>
                        toggleFavourite(chargingStation?.id, isFavourite)
                    }
                    onClick={() => handleCardNavigation(chargingStation)}
                />
            );
        });

    // Fetch more stations on scroll
    const loadMore = () => {
        if (!loading && hasMore && page > 0) {
            dispatch(
                getallStations({
                    connector_type: activeFilter,
                    station_name: stationName,
                    favourites_only: favouritesOnly,
                    page: page,
                })
            );
        }
    };

    // Availability is live connector state — reload it rather than keeping
    // what we fetched when the screen first opened.
    const reloadStations = useCallback(
        () =>
            dispatch(
                filterStations({
                    connector_type: activeFilter,
                    station_name: stationName,
                    favourites_only: favouritesOnly,
                })
            ),
        [dispatch, activeFilter, stationName, favouritesOnly]
    );

    useRefreshOnFocus(reloadStations);
    const { pullProps, pullIndicator } = usePullToRefresh(reloadStations);

    // Update stations by stationName, ConnectorType or the favourites filter
    useEffect(() => {
        const debouncedStationName = setTimeout(() => {
            dispatch(
                filterStations({
                    connector_type: activeFilter,
                    station_name: stationName,
                    favourites_only: favouritesOnly,
                })
            );
        }, 500);
        return () => clearTimeout(debouncedStationName);
    }, [activeFilter, stationName, favouritesOnly]);

    // Fetch all the connector Standards
    useEffect(() => {
        dispatch(fetchConnectorStandards());
    }, []);
    return (
        <div>
            <main className="SearchStations" {...pullProps}>
                <StationHeader />
                <div className="SearchStations__Input">
                    <Input
                        label={'Search Stations'}
                        value={stationName}
                        onValueChange={handleStationName}
                    />
                </div>
                <div className="SearchStations__FilterContainer">
                    <FilterBtn
                        text={'All'}
                        handleClick={() => setSelectedChip(ALL_CHIP)}
                        activeStatus={selectedChip === ALL_CHIP}
                    />
                    <FilterBtn
                        text={'Favourites'}
                        handleClick={() => setSelectedChip(FAVOURITES_CHIP)}
                        activeStatus={favouritesOnly}
                    />
                    {connectorStandards?.map((item) => (
                        <FilterBtn
                            // The standard itself is the stable identity.
                            key={item}
                            // Chips read prettily; `connector_type` still goes
                            // out as the raw API value.
                            text={prettyConnectorStandard(item)}
                            id={item}
                            handleClick={() => setSelectedChip(item)}
                            activeStatus={selectedChip === item}
                        />
                    ))}
                </div>

                <div>
                    {pullIndicator}
                    <InfiniteScroll
                        pageStart={0}
                        loadMore={loadMore}
                        hasMore={hasMore}
                        loader={Preloader()}
                    >
                        <div className="SearchStations__Cards">
                            {allStations?.length > 0 ? (
                                renderStationCards()
                            ) : loading ? (
                                <h1>Loading Stations....</h1>
                            ) : (
                                <h1>
                                    {favouritesOnly
                                        ? 'No favourite stations yet'
                                        : 'No Stations Available'}
                                </h1>
                            )}
                        </div>
                    </InfiniteScroll>
                </div>
            </main>
        </div>
    );
};

export default SearchStations;
